// @ts-strict-ignore
import { useQuery } from "@apollo/client";
import { DashboardCard } from "@dashboard/components/Card";
import { useCustomerDetails } from "@dashboard/customers/hooks/useCustomerDetails";
import { getUserName } from "@dashboard/misc";
import { CardContent, Typography } from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import { Button, Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import { useUserPermissions } from "@dashboard/auth/hooks/useUserPermissions";
import { PermissionEnum } from "@dashboard/graphql";
import { CustomerWallet } from "../../types";
import { CUSTOMER_WALLET_QUERY } from "../../walletQueries";
import WalletLedgerDialog from "../WalletLedgerDialog/WalletLedgerDialog";
import WalletStaffAdjustmentDialog from "../WalletStaffAdjustmentDialog/WalletStaffAdjustmentDialog";

const useStyles = makeStyles(
  (theme: any) => ({
    balanceRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(1),
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      paddingTop: theme.spacing(1),
      borderTop: `1px dashed ${theme.palette.divider}`,
    },
    actions: {
      display: "flex",
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    realAmount: {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    promoAmount: {
      fontWeight: 600,
      color: theme.palette.secondary.main,
    },
    totalAmount: {
      fontWeight: 700,
      fontSize: "1.15rem",
      color: theme.palette.primary.main,
    },
  }),
  { name: "CustomerWalletCard" },
);

interface CustomerWalletCardProps {
  customerId?: string;
}

export const CustomerWalletCard: React.FC<CustomerWalletCardProps> = ({ customerId }) => {
  const userPermissions = useUserPermissions();
  const hasWalletPermission = userPermissions?.some(p => p.code === PermissionEnum.MANAGE_WALLET);

  if (!hasWalletPermission) {
    return null;
  }

  const classes = useStyles();
  const customerDetails = useCustomerDetails();
  const customer = customerDetails?.customer?.user;
  const targetUserId = customerId || customer?.id;
  const customerName = customer ? getUserName(customer) : "";

  const [openAdjustmentDialog, setOpenAdjustmentDialog] = useState(false);
  const [openLedgerDialog, setOpenLedgerDialog] = useState(false);

  const { data, loading, refetch } = useQuery(CUSTOMER_WALLET_QUERY, {
    variables: { userId: targetUserId },
    skip: !targetUserId,
    fetchPolicy: "cache-and-network",
  });

  const wallet: CustomerWallet | undefined = data?.wallet;

  if (loading && !wallet) {
    return (
      <DashboardCard>
        <DashboardCard.Header>
          <DashboardCard.Title>Digital Wallet</DashboardCard.Title>
        </DashboardCard.Header>
        <CardContent>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </CardContent>
      </DashboardCard>
    );
  }

  const realBalance = wallet ? Number(wallet.currentRealBalance) : 0;
  const promoBalance = wallet ? Number(wallet.currentPromoBalance) : 0;
  const totalBalance = wallet ? Number(wallet.totalBalance) : 0;
  const reservedBalance = wallet
    ? Number(wallet.reservedRealBalance || 0) + Number(wallet.reservedPromoBalance || 0)
    : 0;

  return (
    <>
      <DashboardCard>
        <DashboardCard.Header>
          <DashboardCard.Title>Digital Wallet</DashboardCard.Title>
        </DashboardCard.Header>
        <CardContent>
          <div className={classes.balanceRow}>
            <Typography variant="body2" color="textSecondary">
              Real Balance (Deposited):
            </Typography>
            <Typography variant="body1" className={classes.realAmount}>
              ₹{realBalance.toFixed(2)}
            </Typography>
          </div>

          <div className={classes.balanceRow}>
            <Typography variant="body2" color="textSecondary">
              Promotional / Bonus:
            </Typography>
            <Typography variant="body1" className={classes.promoAmount}>
              ₹{promoBalance.toFixed(2)}
            </Typography>
          </div>

          {reservedBalance > 0 && (
            <div className={classes.balanceRow}>
              <Typography variant="body2" color="textSecondary">
                Reserved (In-flight Order):
              </Typography>
              <Typography variant="body2" style={{ color: "#d97706", fontWeight: 600 }}>
                ₹{reservedBalance.toFixed(2)}
              </Typography>
            </div>
          )}

          <div className={classes.totalRow}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Available Balance:
            </Typography>
            <Typography variant="h6" className={classes.totalAmount}>
              ₹{totalBalance.toFixed(2)}
            </Typography>
          </div>

          <div className={classes.actions}>
            <Button type="button" variant="primary" size="small" onClick={() => setOpenAdjustmentDialog(true)}>
              Adjust Balance
            </Button>
            <Button type="button" variant="secondary" size="small" onClick={() => setOpenLedgerDialog(true)}>
              Ledger History
            </Button>
          </div>
        </CardContent>
      </DashboardCard>

      {targetUserId && (
        <>
          <WalletStaffAdjustmentDialog
            open={openAdjustmentDialog}
            onClose={() => setOpenAdjustmentDialog(false)}
            userId={targetUserId}
            customerName={customerName}
            onSuccess={() => refetch()}
          />

          <WalletLedgerDialog
            open={openLedgerDialog}
            onClose={() => setOpenLedgerDialog(false)}
            customerName={customerName}
            entries={wallet?.ledgerEntries || []}
          />
        </>
      )}
    </>
  );
};

export default CustomerWalletCard;
